import { call, put, select, cancelled } from 'redux-saga/effects';
import { TApiResponse, IApiStateEntry } from '../types';
import * as actions from '../actions';
import { fetchApi } from './fetchApi';
import { fnv1aHash } from '../../../../utils/fnv1aHash';
import { RootState } from '../../..';

export function* handleApiRequest({ payload }: ReturnType<typeof actions.ApiRequest>): Generator {
  let key = payload.key || 'unknown-request';

  if (!payload.key) {
    try {
      const confString = JSON.stringify(payload);
      key = fnv1aHash(confString);
    } catch {
      yield put(
        actions.ApiRequestError({
          key,
          error: {
            status: 400,
            message: 'API request key error',
            details: 'Error request key generation by parameters',
          },
        })
      );
      return;
    }
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const entry = yield select((state: RootState) => state.api[key]);
    const status = (entry as IApiStateEntry | undefined)?.status || 'init';

    // Проверка: если есть force - запрашиваем всегда
    // Если нет force - запрашиваем только если статус init или истёк TTL
    const entryTyped = entry as IApiStateEntry | undefined;
    const isExpired =
      entryTyped?.timestamp && entryTyped?.ttl
        ? Date.now() - entryTyped.timestamp > entryTyped.ttl
        : false;

    const shouldRequest = payload.force || status === 'init' || isExpired;

    if (!shouldRequest) return;

    // Устанавливаем статус progress до выполнения запроса
    yield put(actions.ApiRequestProgress({ key }));

    const result = (yield call(fetchApi, payload)) as Partial<TApiResponse>;

    // Проверка на отмену после получения ответа
    if (yield cancelled()) {
      return;
    }

    if (result.error) {
      yield put(actions.ApiRequestError({ key, error: result.error }));
      return;
    }

    yield put(
      actions.ApiRequestSucceed({
        key,
        data: result.data,
        meta: result.meta,
      })
    );
  } catch (error) {
    // Проверка на отмену
    if (yield cancelled()) {
      // Запрос был отменён (например, размонтирован компонент)
      // Не отправляем ошибку, чтобы не обновлять состояние
      return;
    }

    const safeError = {
      status: 400,
      message: 'Request error',
      details: error instanceof Error ? error.message : String(error),
    };
    yield put(
      actions.ApiRequestError({
        key,
        error: safeError,
      })
    );
  }
}
